'use client';

import * as Headless from '@headlessui/react';
import { cva, type VariantProps } from 'class-variance-authority';
import { LayoutGroup, motion } from 'framer-motion';
import React, { forwardRef, useId } from 'react';
import { TouchTarget } from './button';
import { Link } from './link';
import { cn } from '../../lib/utils';

const sidebarVariants = cva(['flex h-full min-h-0 flex-col']);

export interface SidebarProps
  extends React.ComponentPropsWithoutRef<'nav'>,
    VariantProps<typeof sidebarVariants> {
  className?: string;
  variant?:
    | 'default'
    | 'destructive'
    | 'outline'
    | 'secondary'
    | 'accent'
    | 'muted'
    | 'ghost'
    | 'link'
    | 'plain'
    | 'crimson'
    | 'mint'
    | 'blue'
    | 'black'
    | 'subtle'
    | 'underline'
    | 'nav'
    | 'primary';
}

export function Sidebar({
  className,
  variant = 'default',
  ...props
}: SidebarProps) {
  return (
    <nav {...props} className={cn(sidebarVariants(), className)} />
  );
}

const sidebarHeaderVariants = cva([
  'flex flex-col border-b border-border p-4 dark:border-border [&>[data-slot=section]+[data-slot=section]]:mt-2.5',
]);

export interface SidebarHeaderProps
  extends React.ComponentPropsWithoutRef<'div'>,
    VariantProps<typeof sidebarHeaderVariants> {
  className?: string;
}

export function SidebarHeader({
  className,
  ...props
}: SidebarHeaderProps) {
  return (
    <div
      {...props}
      className={cn(sidebarHeaderVariants(), className)}
    />
  );
}

const sidebarBodyVariants = cva([
  'flex flex-1 flex-col overflow-y-auto p-4 [&>[data-slot=section]+[data-slot=section]]:mt-8',
]);

export interface SidebarBodyProps
  extends React.ComponentPropsWithoutRef<'div'>,
    VariantProps<typeof sidebarBodyVariants> {
  className?: string;
}

export function SidebarBody({
  className,
  ...props
}: SidebarBodyProps) {
  return (
    <div
      {...props}
      className={cn(sidebarBodyVariants(), className)}
    />
  );
}

const sidebarFooterVariants = cva([
  'flex flex-col border-t border-border p-4 dark:border-border [&>[data-slot=section]+[data-slot=section]]:mt-2.5',
]);

export interface SidebarFooterProps
  extends React.ComponentPropsWithoutRef<'div'>,
    VariantProps<typeof sidebarFooterVariants> {
  className?: string;
}

export function SidebarFooter({
  className,
  ...props
}: SidebarFooterProps) {
  return (
    <div
      {...props}
      className={cn(sidebarFooterVariants(), className)}
    />
  );
}

const sidebarSectionVariants = cva(['flex flex-col gap-0.5']);

export interface SidebarSectionProps
  extends React.ComponentPropsWithoutRef<'div'>,
    VariantProps<typeof sidebarSectionVariants> {
  className?: string;
}

export function SidebarSection({
  className,
  ...props
}: SidebarSectionProps) {
  const id = useId();

  return (
    <LayoutGroup id={id}>
      <div
        {...props}
        data-slot="section"
        className={cn(sidebarSectionVariants(), className)}
      />
    </LayoutGroup>
  );
}

const sidebarDividerVariants = cva([
  'my-4 border-t border-border lg:-mx-4 dark:border-border',
]);

export interface SidebarDividerProps
  extends React.ComponentPropsWithoutRef<'hr'>,
    VariantProps<typeof sidebarDividerVariants> {
  className?: string;
}

export function SidebarDivider({
  className,
  ...props
}: SidebarDividerProps) {
  return (
    <hr
      {...props}
      className={cn(sidebarDividerVariants(), className)}
    />
  );
}

const sidebarSpacerVariants = cva(['mt-8 flex-1']);

export interface SidebarSpacerProps
  extends React.ComponentPropsWithoutRef<'div'>,
    VariantProps<typeof sidebarSpacerVariants> {
  className?: string;
}

export function SidebarSpacer({
  className,
  ...props
}: SidebarSpacerProps) {
  return (
    <div
      aria-hidden="true"
      {...props}
      className={cn(sidebarSpacerVariants(), className)}
    />
  );
}

const sidebarHeadingVariants = cva([
  'mb-1 px-2 text-xs/6 font-medium text-muted-foreground dark:text-muted-foreground',
]);

export interface SidebarHeadingProps
  extends React.ComponentPropsWithoutRef<'h3'>,
    VariantProps<typeof sidebarHeadingVariants> {
  className?: string;
}

export function SidebarHeading({
  className,
  ...props
}: SidebarHeadingProps) {
  return (
    <h3
      {...props}
      className={cn(sidebarHeadingVariants(), className)}
    />
  );
}

const sidebarItemVariants = cva(
  [
    // Base
    'relative flex min-w-0 items-center gap-3 rounded-lg p-2 text-left text-base/6 font-medium text-foreground sm:text-sm/5',
    // Leading icon/icon-only
    '*:data-[slot=icon]:size-6 *:data-[slot=icon]:shrink-0 *:data-[slot=icon]:fill-muted-foreground sm:*:data-[slot=icon]:size-5',
    // Trailing icon (down chevron or similar)
    '*:not-nth-2:last:data-[slot=icon]:ml-auto *:not-nth-2:last:data-[slot=icon]:size-5 sm:*:not-nth-2:last:data-[slot=icon]:size-4',
    // Avatar
    '*:data-[slot=avatar]:-m-0.5 *:data-[slot=avatar]:size-7 *:data-[slot=avatar]:size-6',
    // Hover
    'hover:bg-muted hover:*:data-[slot=icon]:fill-foreground',
    // Active
    'data-[state=active]:bg-muted data-[state=active]:*:data-[slot=icon]:fill-foreground',
    // Disclosure items
    'data-[disclosure-item=true]:pl-6',
  ],
  {
    variants: {
      variant: {
        default:
          'text-foreground data-[current=true]:after:bg-primary',
        destructive:
          'text-destructive data-[current=true]:after:bg-destructive hover:text-destructive',
        outline:
          'text-accent data-[current=true]:after:bg-accent hover:text-accent-foreground',
        secondary:
          'text-secondary data-[current=true]:after:bg-secondary hover:text-secondary',
        accent:
          'text-accent data-[current=true]:after:bg-accent hover:text-accent',
        muted:
          'text-muted data-[current=true]:after:bg-muted hover:text-muted-foreground',
        ghost:
          'text-accent data-[current=true]:after:bg-accent hover:text-accent-foreground',
        link: 'text-primary data-[current=true]:after:bg-primary hover:text-primary/80 hover:underline',
        plain:
          'text-accent data-[current=true]:after:bg-accent hover:text-accent',
        crimson:
          'text-flame data-[current=true]:after:bg-flame hover:text-flame',
        mint: 'text-sunlight data-[current=true]:after:bg-sunlight hover:text-sunlight',
        blue: 'text-ocean data-[current=true]:after:bg-ocean hover:text-ocean',
        black:
          'text-black data-[current=true]:after:bg-black dark:data-[current=true]:after:bg-white hover:text-black dark:hover:text-white',
        subtle:
          'text-muted data-[current=true]:after:bg-muted hover:text-foreground',
        underline:
          'text-primary data-[current=true]:after:bg-primary hover:text-primary underline-offset-4 hover:underline',
        nav: 'text-foreground data-[current=true]:after:bg-foreground/60 hover:text-foreground/80',
        primary:
          'text-primary data-[current=true]:after:bg-primary hover:text-primary',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface SidebarItemProps
  extends VariantProps<typeof sidebarItemVariants> {
  current?: boolean;
  className?: string;
  children: React.ReactNode;
  variant?:
    | 'default'
    | 'destructive'
    | 'outline'
    | 'secondary'
    | 'accent'
    | 'muted'
    | 'ghost'
    | 'link'
    | 'plain'
    | 'crimson'
    | 'mint'
    | 'blue'
    | 'black'
    | 'subtle'
    | 'underline'
    | 'nav'
    | 'primary';
}

export const SidebarItem = forwardRef(function SidebarItem(
  {
    current,
    className,
    children,
    variant = 'default',
    ...props
  }: SidebarItemProps &
    (
      | Omit<Headless.ButtonProps, 'as' | 'className'>
      | Omit<Headless.ButtonProps<typeof Link>, 'as' | 'className'>
    ),
  ref: React.ForwardedRef<HTMLAnchorElement | HTMLButtonElement>
) {
  const classes = cn(
    sidebarItemVariants({ variant }),
    current && 'bg-muted/80 dark:bg-muted/30',
    className
  );

  return (
    <span className={cn('relative', className)}>
      {current && (
        <>
          {/* Left side indicator */}
          <motion.span
            layoutId="current-side-indicator"
            className="absolute left-0 top-0 bottom-0 w-0.5 rounded-full bg-secondary dark:bg-secondary z-10"
          />
        </>
      )}
      {'href' in props ? (
        <Link
          {...props}
          className={classes}
          variant={variant}
          useOwnVariants={false}
          data-current={current ? 'true' : undefined}
          ref={ref as React.ForwardedRef<HTMLAnchorElement>}
        >
          <TouchTarget>{children}</TouchTarget>
        </Link>
      ) : (
        <Headless.Button
          {...props}
          className={cn('cursor-default', classes)}
          data-current={current ? 'true' : undefined}
          ref={ref}
        >
          <TouchTarget>{children}</TouchTarget>
        </Headless.Button>
      )}
    </span>
  );
});

const sidebarLabelVariants = cva(['truncate']);

export interface SidebarLabelProps
  extends React.ComponentPropsWithoutRef<'span'>,
    VariantProps<typeof sidebarLabelVariants> {
  className?: string;
}

export function SidebarLabel({
  className,
  ...props
}: SidebarLabelProps) {
  return (
    <span
      {...props}
      className={cn(sidebarLabelVariants(), className)}
    />
  );
}

export {
  sidebarVariants,
  sidebarHeaderVariants,
  sidebarBodyVariants,
  sidebarFooterVariants,
  sidebarSectionVariants,
  sidebarDividerVariants,
  sidebarSpacerVariants,
  sidebarHeadingVariants,
  sidebarItemVariants,
  sidebarLabelVariants,
};
